/**
 * Form validation utilities for KYC and Audit applications
 */

export interface ValidationResult {
    valid: boolean
    error?: string
}

/**
 * Validate email address
 */
export function validateEmail(email: string): ValidationResult {
    if (!email) {
        return { valid: false, error: "Email is required" }
    }

    // RFC 5322 simplified regex for email validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

    if (!emailRegex.test(email)) {
        return { valid: false, error: "Invalid email format" }
    }

    // Check for common typos in domain
    const domain = email.split('@')[1]?.toLowerCase()
    const commonTypos: Record<string, string> = {
        'gmial.com': 'gmail.com',
        'gmal.com': 'gmail.com',
        'gamil.com': 'gmail.com',
        'hotmal.com': 'hotmail.com',
        'yahooo.com': 'yahoo.com',
        'outlok.com': 'outlook.com'
    }

    if (domain && commonTypos[domain]) {
        return { valid: false, error: `Did you mean ${email.split('@')[0]}@${commonTypos[domain]}?` }
    }

    return { valid: true }
}

/**
 * Validate Telegram username
 * Must start with @ and contain only alphanumeric and underscores
 * Length: 5-32 characters (excluding @)
 */
export function validateTelegram(telegram: string): ValidationResult {
    if (!telegram) {
        return { valid: true } // Optional field
    }

    const trimmed = telegram.trim()

    // Must start with @
    if (!trimmed.startsWith('@')) {
        return { valid: false, error: "Telegram username must start with @" }
    }

    // Check minimum length (@ + at least 5 characters)
    if (trimmed.length < 6) {
        return { valid: false, error: "Username too short (min 5 characters after @)" }
    }

    // Check maximum length (@ + max 32 characters)
    if (trimmed.length > 33) {
        return { valid: false, error: "Username too long (max 32 characters after @)" }
    }

    // Check format: must start with letter after @, only alphanumeric and underscores
    const telegramRegex = /^@[a-zA-Z][a-zA-Z0-9_]{4,31}$/

    if (!telegramRegex.test(trimmed)) {
        return { valid: false, error: "Invalid format: use letters, numbers, underscores only" }
    }

    return { valid: true }
}

/**
 * Validate website URL
 */
export function validateWebsite(url: string): ValidationResult {
    if (!url) {
        return { valid: true } // Optional field
    }

    // Add https:// if no protocol
    let urlToTest = url
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        urlToTest = `https://${url}`
    }

    try {
        const parsedUrl = new URL(urlToTest)

        // Check for valid hostname
        if (!parsedUrl.hostname.includes('.')) {
            return { valid: false, error: "Invalid website domain" }
        }

        // Check for localhost (not allowed)
        if (parsedUrl.hostname === 'localhost' || parsedUrl.hostname === '127.0.0.1') {
            return { valid: false, error: "Localhost URLs are not allowed" }
        }

        return { valid: true }
    } catch {
        return { valid: false, error: "Invalid URL format" }
    }
}

/**
 * Validate project name
 */
export function validateProjectName(name: string): ValidationResult {
    if (!name) {
        return { valid: false, error: "Project name is required" }
    }

    if (name.length < 2) {
        return { valid: false, error: "Project name too short (min 2 characters)" }
    }

    if (name.length > 100) {
        return { valid: false, error: "Project name too long (max 100 characters)" }
    }

    return { valid: true }
}

/**
 * Validate full name
 */
export function validateFullName(name: string): ValidationResult {
    if (!name) {
        return { valid: false, error: "Full name is required" }
    }

    if (name.trim().length < 2) {
        return { valid: false, error: "Name too short (min 2 characters)" }
    }

    if (name.length > 100) {
        return { valid: false, error: "Name too long (max 100 characters)" }
    }

    // Check for at least one letter
    if (!/[a-zA-Z]/.test(name)) {
        return { valid: false, error: "Name must contain letters" }
    }

    return { valid: true }
}

/**
 * Validate project description
 */
export function validateDescription(description: string): ValidationResult {
    if (!description) {
        return { valid: true } // Optional field
    }

    if (description.length > 2000) {
        return { valid: false, error: "Description too long (max 2000 characters)" }
    }

    return { valid: true }
}

/**
 * Validate team member email
 */
export function validateTeamMemberEmail(email: string, founderEmail: string): ValidationResult {
    if (!email) {
        return { valid: false, error: "Team member email is required" }
    }

    const emailValidation = validateEmail(email)
    if (!emailValidation.valid) {
        return emailValidation
    }

    // Check that it's not the same as founder email
    if (email.toLowerCase() === founderEmail.toLowerCase()) {
        return { valid: false, error: "Cannot be same as founder email" }
    }

    return { valid: true }
}

/**
 * Validate Ethereum/EVM address
 */
export function validateEvmAddress(address: string): ValidationResult {
    if (!address) {
        return { valid: true } // Optional field
    }

    const evmRegex = /^0x[a-fA-F0-9]{40}$/

    if (!evmRegex.test(address)) {
        return { valid: false, error: "Invalid EVM wallet address" }
    }

    return { valid: true }
}

/**
 * Validate GitHub/GitLab URL
 */
export function validateRepoUrl(url: string): ValidationResult {
    if (!url) {
        return { valid: true } // Optional field
    }

    const websiteValidation = validateWebsite(url)
    if (!websiteValidation.valid) {
        return websiteValidation
    }

    // Check for common repo hosts
    const validHosts = ['github.com', 'gitlab.com', 'bitbucket.org', 'codeberg.org']
    try {
        const parsedUrl = new URL(url.startsWith('http') ? url : `https://${url}`)
        if (!validHosts.some(host => parsedUrl.hostname.includes(host))) {
            return { valid: false, error: "Please use GitHub, GitLab, or Bitbucket URL" }
        }
    } catch {
        return { valid: false, error: "Invalid repository URL" }
    }

    return { valid: true }
}

/**
 * Validate Twitter/X username
 */
export function validateTwitter(handle: string): ValidationResult {
    if (!handle) {
        return { valid: true } // Optional field
    }

    // Remove @ if present
    const username = handle.startsWith('@') ? handle.slice(1) : handle

    // Twitter username rules: 4-15 characters, alphanumeric and underscores
    const twitterRegex = /^[a-zA-Z0-9_]{4,15}$/

    if (!twitterRegex.test(username)) {
        if (username.length < 4) {
            return { valid: false, error: "Username too short (min 4 characters)" }
        }
        if (username.length > 15) {
            return { valid: false, error: "Username too long (max 15 characters)" }
        }
        return { valid: false, error: "Invalid Twitter/X username format" }
    }

    return { valid: true }
}

/**
 * Validate Discord invite link or username
 */
export function validateDiscord(discord: string): ValidationResult {
    if (!discord) {
        return { valid: true } // Optional field
    }

    // Check if it's a Discord invite link
    if (discord.includes('discord.gg') || discord.includes('discord.com/invite')) {
        return { valid: true }
    }

    // Check if it's a Discord username (New format: username, Old format: username#0000)
    const newUsernameRegex = /^[a-z0-9._]{2,32}$/
    const oldUsernameRegex = /^.{2,32}#[0-9]{4}$/

    if (!newUsernameRegex.test(discord.toLowerCase()) && !oldUsernameRegex.test(discord)) {
        return { valid: false, error: "Invalid Discord username or invite link" }
    }

    return { valid: true }
}

/**
 * Validate contract address (same as EVM address for now)
 */
export function validateContractAddress(address: string): ValidationResult {
    if (!address) {
        return { valid: false, error: "Contract address is required" }
    }

    return validateEvmAddress(address)
}

/**
 * All-in-one form validator
 */
export interface FormValidationErrors {
    [key: string]: string
}

export function validateKYCForm(data: {
    name: string
    email: string
    projectName: string
    telegram?: string
    website?: string
    description?: string
    teamMembers?: { name: string; email: string }[]
}): FormValidationErrors {
    const errors: FormValidationErrors = {}

    const nameResult = validateFullName(data.name)
    if (!nameResult.valid) errors.name = nameResult.error!

    const emailResult = validateEmail(data.email)
    if (!emailResult.valid) errors.email = emailResult.error!

    const projectResult = validateProjectName(data.projectName)
    if (!projectResult.valid) errors.projectName = projectResult.error!

    if (data.telegram) {
        const telegramResult = validateTelegram(data.telegram)
        if (!telegramResult.valid) errors.telegram = telegramResult.error!
    }

    if (data.website) {
        const websiteResult = validateWebsite(data.website)
        if (!websiteResult.valid) errors.website = websiteResult.error!
    }

    if (data.description) {
        const descResult = validateDescription(data.description)
        if (!descResult.valid) errors.description = descResult.error!
    }

    // Validate team members
    if (data.teamMembers) {
        data.teamMembers.forEach((member, idx) => {
            if (member.name) {
                const nameResult = validateFullName(member.name)
                if (!nameResult.valid) errors[`teamMember${idx}Name`] = nameResult.error!
            }
            if (member.email) {
                const emailResult = validateTeamMemberEmail(member.email, data.email)
                if (!emailResult.valid) errors[`teamMember${idx}Email`] = emailResult.error!
            }
        })
    }

    return errors
}
